import type { zoomError } from '@lib/zoom/types';
import {
  ZOOM_VALIDATION_ERROR,
  EMPTY_LINK_ERROR_CODE,
  INVALID_LINK_ERROR_CODE,
  REGISTRATION_LINK_ERROR_CODE,
  EVENT_LINK_ERROR_CODE
} from '@lib/zoom/constants';

type ZoomErrorCodes = Record<number | string, zoomError>;

export const ZOOM_ERROR_CODES: ZoomErrorCodes = {
  200: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Not support start meeting via tokens',
    errorCode: 200,
    message: 'This meeting/webinar requires StreamAlive to be authorized by Zoom to connect. Additionally, the user that authorized it needs to be added to the Zoom meeting as alternative host.',
    helpUrl: 'https://help.streamalive.com/en/articles/11469104-authorizing-streamalive-access-to-your-zoom'
  },
  3706: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'The meeting number is incorrect.',
    errorCode: 3706,
    message: 'Please double check your meeting link. Zoom reported that the meeting number you provided is incorrect.',
    helpUrl: 'https://help.streamalive.com/en/articles/10473711-zoom-where-to-find-your-zoom-meeting-or-webinar-panelist',
    isLinkError: true,
  },
  3707: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'The meeting number is not found.',
    errorCode: 3707,
    message: 'Please double check your meeting link. Zoom reported that it couldn\'t find a meeting with the number you provided.',
    helpUrl: 'https://help.streamalive.com/en/articles/10473711-zoom-where-to-find-your-zoom-meeting-or-webinar-panelist',
    isLinkError: true,
  },
  // Passcode is carried in the link's `pwd`, so a host who resets it after the
  // link was saved lands here. Non-retryable in the bot's join ladder.
  3004: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Meeting password is wrong.',
    errorCode: 3004,
    message: 'The passcode in this meeting link is wrong, usually because the host reset it after the link was copied. Copy the invite link from Zoom again and paste it in.',
    helpUrl: 'https://help.streamalive.com/en/articles/9333762-zoom-how-to-use-streamalive-with-zoom-meetings',
    isLinkError: true,
  },
  // "Join before host" is off and the host hasn't started yet. Nothing about the
  // link or the account is wrong — StreamAlive is simply early.
  3008: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Meeting has not started',
    errorCode: 3008,
    message: 'This meeting hasn\'t started yet, and it isn\'t set to let anyone join before the host. Start the meeting in Zoom first, then connect StreamAlive.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide',
  },
  3009: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Be removed.',
    errorCode: 3009,
    message: 'It looks like the meeting host removed the StreamAlive bot. If this was unintentional please re-connect StreamAlive to your meeting.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_2bf3c65265'
  },
  3051: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'Require login',
    errorCode: 3051,
    message: 'StreamAlive couldn\'t join your meeting because it hasn\'t been authorized with your Zoom account. Follow these instructions to set up the authorization.',
    helpUrl: 'https://help.streamalive.com/en/articles/11469104-authorizing-streamalive-access-to-your-zoom'
  },
  3099: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'This webinar requires registration.',
    errorCode: 3099,
    message: 'This Zoom webinar requires a user to register before allowing access. Please register for the webinar and then paste in the webinar join link once you have registered.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_92f7a955f9',
    isLinkError: true,
  },
  // Zoom sends 4008 `participant exist` for two different situations, and this
  // map is keyed by code alone so it can't tell them apart: the bot builds the
  // real copy from its own map, which overlays a registrant-token variant (see
  // ZOOM_ERROR_CODES_REGISTRANT in apps/zoom-sdk/src/lib/error-codes.ts) and
  // sends `isLinkError` along with it. This entry is the fallback for payloads
  // that arrive without that flag, so it names both causes and blames neither
  // the link nor the meeting.
  4008: {
    type: 'JOIN_MEETING_FAILED',
    reason: 'participant exist',
    errorCode: 4008,
    message: 'Zoom says this link is already in use. If it is a registration link, the person who registered is already in the meeting and Zoom allows them one device only — register StreamAlive as its own attendee and use that join link. Otherwise an earlier StreamAlive connection is still in the meeting or its waiting room: remove it, then connect again.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide',
  },
  [EMPTY_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Empty Meeting Link Entered',
    errorCode: EMPTY_LINK_ERROR_CODE,
    message: 'You haven\'t entered a Zoom meeting link. Please follow these instructions to learn how to find your Zoom meeting link.',
    helpUrl: 'https://help.streamalive.com/en/articles/9333762-zoom-how-to-use-streamalive-with-zoom-meetings',
    isLinkError: true,
  },
  [INVALID_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Invalid Link Entered',
    errorCode: INVALID_LINK_ERROR_CODE,
    message: 'The meeting link you entered has been rejected by Zoom. Please follow these instructions to learn how to find your Zoom meeting link.',
    helpUrl: 'https://help.streamalive.com/en/articles/9333762-zoom-how-to-use-streamalive-with-zoom-meetings',
    isLinkError: true,
  },
  [REGISTRATION_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Registration Link Entered',
    errorCode: REGISTRATION_LINK_ERROR_CODE,
    message: 'The Zoom link you entered is a registration link, not the meeting link. Follow these instructions to find the correct Zoom meeting link.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_ff7008544e',
    isLinkError: true,
  },
  [EVENT_LINK_ERROR_CODE]: {
    type: ZOOM_VALIDATION_ERROR,
    reason: 'Event Link Entered',
    errorCode: EVENT_LINK_ERROR_CODE,
    message: 'The Zoom link you entered is a Zoom events link, not a Zoom meeting link. Follow these instructions to find the correct Zoom events link.',
    helpUrl: 'https://help.streamalive.com/en/articles/11332308-streamalive-zoom-connection-troubleshooting-guide#h_afb22da341',
    isLinkError: true,
  }
};
